import { useState } from 'react';
import SectionTitle from '@/components/base/SectionTitle';
import { siteInfo } from '@/mocks/site';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dahgblboh653ivfvnua0';

const clueTypes = ['娱乐传闻', '商业传闻', '科技爆料', '社会热点', '生活消费', '体坛风云'];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') ?? '').trim();

    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交未成功，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，线索未能送达，请稍后重试。');
    }
  };

  return (
    <section id="contact" className="w-full border-b border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <SectionTitle
              eyebrow="Submit A Clue"
              title="你看到一条可疑爆料？"
              desc="把原始链接、完整截图或视频发给我们，注明你看到它的时间和来源。越接近一手材料，求证就越快。"
            />

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-100/60 p-4">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="font-label text-xs text-foreground-600">求证邮箱</p>
                  <a href={`mailto:${siteInfo.email}`} className="font-heading text-sm font-semibold text-foreground-950 hover:text-primary-600">
                    {siteInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-100/60 p-4">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <p className="font-label text-xs text-foreground-600">受理时间</p>
                  <p className="font-heading text-sm font-semibold text-foreground-950">{siteInfo.workingTime}</p>
                </div>
              </div>

              <p className="rounded-xl border border-secondary-200 bg-secondary-50 p-4 text-sm leading-relaxed text-secondary-900">
                我们不会公开未经授权的私人信息，也请你在提交时避免附带与公共议题无关的个人资料。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-readdy-form
              id="clue-form"
              onSubmit={handleSubmit}
              className="rounded-2xl border border-background-200 bg-background-100/50 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label htmlFor="clue-email" className="font-label text-sm text-foreground-800">
                    你的邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="clue-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="clue-type" className="font-label text-sm text-foreground-800">
                    线索类型
                  </label>
                  <select
                    id="clue-type"
                    name="clue_type"
                    defaultValue={clueTypes[0]}
                    className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    {clueTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <label htmlFor="clue-link" className="font-label text-sm text-foreground-800">
                  原始链接
                </label>
                <input
                  id="clue-link"
                  name="source_link"
                  type="text"
                  placeholder="https://… 或直接描述你看到的平台"
                  className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <label htmlFor="clue-detail" className="font-label text-sm text-foreground-800">
                  事件概述 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="clue-detail"
                  name="detail"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述传闻原文、首次看到的时间，以及你认为需要核对的关键点（限 500 字）"
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              <div className="aux-field-slot" aria-hidden="true">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                  {status === 'submitting' ? '提交中…' : '提交线索'}
                </button>
                <p className="font-label text-xs text-foreground-500">我们会在 3 个工作日内查看并回复</p>
              </div>

              {status === 'success' ? (
                <p
                  role="status"
                  className="mt-5 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900"
                >
                  <i className="ri-checkbox-circle-line mt-0.5"></i>
                  线索已收到，感谢你让信息更清楚一点。我们会尽快核对并回复。
                </p>
              ) : null}

              {status === 'error' ? (
                <p
                  role="alert"
                  className="mt-5 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800"
                >
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {formError}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}